'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { getWrapperMaxWidth } from '@/cms/utils';
import { cn } from '@/lib/utils';

import './styles.css';

export type CarouselRenderItemInfo<ItemType> = {
  item: ItemType;
  index: number;
};

export type CarouselRenderItem<ItemType> = ({
  item,
  index,
}: CarouselRenderItemInfo<ItemType>) => React.ReactElement<any> | null;

export type CarouselHandle = {
  slideTo: (index: number) => void;
  slideToLoop: (index: number) => void;
  slideNext: () => void;
  slidePrev: () => void;
  readonly realIndex: number;
};

export type CarouselConfig = {
  loop?: boolean;
  speed?: number;
  allowTouchMove?: boolean;
  autoplay?:
    | {
        delay?: number;
        disableOnInteraction?: boolean;
        pauseOnMouseEnter?: boolean;
      }
    | false;
  onSlideChange?: (carousel: CarouselHandle) => void;
  onSwiper?: (carousel: CarouselHandle) => void;
  onRealIndexChange?: (carousel: { realIndex: number }) => void;
  'aria-label'?: string;
  role?: string;
};

export type CarouselProps<ItemType> = {
  config: CarouselConfig;
  items: ItemType[];
  renderItem: CarouselRenderItem<ItemType>;
  sideGradient?: boolean;
  classNames?: {
    root?: string;
    swiper?: string;
    swiperWrapper?: string;
    swiperSlide?: string;
  };
};

function CarouselInner<ItemType>(
  { config, items, renderItem, classNames }: CarouselProps<ItemType>,
  ref: React.ForwardedRef<{ swiper: CarouselHandle } | null>
) {
  const {
    loop = false,
    speed = 300,
    autoplay,
    onSlideChange,
    onSwiper,
    onRealIndexChange,
  } = config;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const trackRef = useRef<HTMLUListElement>(null);
  const autoplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPausedRef = useRef(false);
  const interactionDisabledRef = useRef(false);

  const totalSlides = items.length;

  const transitionSafetyRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const goTo = useCallback(
    (index: number, animate = true) => {
      if (isTransitioning || totalSlides === 0) return;

      let target = index;
      if (loop) {
        target = ((index % totalSlides) + totalSlides) % totalSlides;
      } else {
        target = Math.max(0, Math.min(index, totalSlides - 1));
      }

      if (target === currentIndex) return;

      if (animate && speed > 0) {
        setIsTransitioning(true);
        if (transitionSafetyRef.current)
          clearTimeout(transitionSafetyRef.current);
        transitionSafetyRef.current = setTimeout(
          () => setIsTransitioning(false),
          speed + 50
        );
      }
      setCurrentIndex(target);
    },
    [isTransitioning, totalSlides, loop, currentIndex, speed]
  );

  const handleRef = useRef<CarouselHandle>(null!);

  // Keep the handle in sync
  handleRef.current = {
    slideTo: (i: number) => goTo(i),
    slideToLoop: (i: number) => goTo(i),
    slideNext: () => goTo(currentIndex + 1),
    slidePrev: () => goTo(currentIndex - 1),
    get realIndex() {
      return currentIndex;
    },
  };

  useImperativeHandle(
    ref,
    () => ({
      get swiper() {
        return handleRef.current;
      },
    }),
    []
  );

  // Proxy reads through handleRef so callbacks always see latest index
  const getHandle = useCallback((): CarouselHandle => {
    return {
      slideTo: (i) => handleRef.current.slideTo(i),
      slideToLoop: (i) => handleRef.current.slideToLoop(i),
      slideNext: () => handleRef.current.slideNext(),
      slidePrev: () => handleRef.current.slidePrev(),
      get realIndex() {
        return handleRef.current.realIndex;
      },
    };
  }, []);

  // Notify onSwiper on mount
  useEffect(() => {
    onSwiper?.(getHandle());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notify callbacks on index change
  useEffect(() => {
    onSlideChange?.(getHandle());
    onRealIndexChange?.({ realIndex: currentIndex });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  // Transition end
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const handleEnd = () => {
      if (transitionSafetyRef.current) {
        clearTimeout(transitionSafetyRef.current);
        transitionSafetyRef.current = null;
      }
      setIsTransitioning(false);
    };
    track.addEventListener('transitionend', handleEnd);
    return () => track.removeEventListener('transitionend', handleEnd);
  }, []);

  // Autoplay
  const clearAutoplay = useCallback(() => {
    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
  }, []);

  const startAutoplay = useCallback(() => {
    if (!autoplay) return;
    if (interactionDisabledRef.current) return;

    clearAutoplay();

    const delay = autoplay.delay ?? 3000;
    autoplayTimerRef.current = setTimeout(() => {
      if (!isPausedRef.current && !interactionDisabledRef.current) {
        goTo(currentIndex + 1);
      }
    }, delay);
  }, [autoplay, currentIndex, clearAutoplay, goTo]);

  useEffect(() => {
    startAutoplay();
    return clearAutoplay;
  }, [startAutoplay, clearAutoplay]);

  const handleMouseEnter = () => {
    if (
      autoplay &&
      typeof autoplay === 'object' &&
      autoplay.pauseOnMouseEnter
    ) {
      isPausedRef.current = true;
      clearAutoplay();
    }
  };

  const handleMouseLeave = () => {
    if (
      autoplay &&
      typeof autoplay === 'object' &&
      autoplay.pauseOnMouseEnter
    ) {
      isPausedRef.current = false;
      startAutoplay();
    }
  };

  const handleInteraction = () => {
    if (
      autoplay &&
      typeof autoplay === 'object' &&
      autoplay.disableOnInteraction
    ) {
      interactionDisabledRef.current = true;
      clearAutoplay();
    }
  };

  return (
    <div
      className={cn(
        getWrapperMaxWidth('full'),
        'flex w-full after:z-10',
        classNames?.root
      )}
    >
      <div
        className={cn('carousel-container', classNames?.swiper)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleInteraction}
        aria-label={config['aria-label']}
        role={config.role}
      >
        <ul
          ref={trackRef}
          className={cn('carousel-track', classNames?.swiperWrapper)}
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
            transition: `transform ${speed}ms ease-in-out`,
          }}
        >
          {items.map((item, i) => (
            <li
              key={i}
              className={cn(
                'carousel-slide',
                classNames?.swiperSlide,
                i === currentIndex && 'carousel-slide-active'
              )}
            >
              {renderItem({ item, index: i })}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export const Carousel = forwardRef(CarouselInner) as <ItemType>(
  props: CarouselProps<ItemType> & {
    ref?: React.Ref<{ swiper: CarouselHandle } | null>;
  }
) => React.ReactElement | null;

export type CarouselInstance = typeof Carousel;
